import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { type TimeOfDay, getTimeOfDay, getSeason } from '../utils/dreamscape';

interface DreamscapeContextValue {
  timeOfDay: TimeOfDay;
  season: string;
}

const DreamscapeContext = createContext<DreamscapeContextValue>({
  timeOfDay: 'morning',
  season: 'winter',
});

export const useDreamscape = () => useContext(DreamscapeContext);

const BODY_CLASSES = [
  'dreamscape-morning',
  'dreamscape-afternoon',
  'dreamscape-evening',
  'dreamscape-night',
] as const;

function setBodyClass(timeOfDay: TimeOfDay) {
  const target = `dreamscape-${timeOfDay}`;
  BODY_CLASSES.forEach((cls) => {
    if (cls === target) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
  });
}

export const DreamscapeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DreamscapeContextValue>(() => ({
    timeOfDay: getTimeOfDay(),
    season: getSeason(),
  }));

  const update = useCallback(() => {
    const timeOfDay = getTimeOfDay();
    setBodyClass(timeOfDay);
    setState({ timeOfDay, season: getSeason() });
  }, []);

  useEffect(() => {
    update();
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [update]);

  return <DreamscapeContext.Provider value={state}>{children}</DreamscapeContext.Provider>;
};
